import TopNav from '@/components/top-nav'
import { PublicEnv } from '@/frontend-env'
import { useDBUser } from '@/hooks/use-db-user'
import '@/styles/globals.css'
import { trpc } from '@/utils/trpc'
import { ClerkProvider as ClerkProviderReact, useUser } from '@clerk/clerk-react'
import { ClerkProvider } from '@clerk/nextjs'
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import mixpanel from 'mixpanel-browser'
import { DefaultSeo } from 'next-seo'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'sonner'
import { LinkedInInsightTag } from 'nextjs-linkedin-insight-tag'

if (typeof window !== 'undefined') {
  posthog.init(PublicEnv.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: PublicEnv.NEXT_PUBLIC_POSTHOG_HOST,
  })
}

function App({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(() => new QueryClient())
  const { data: dbUser } = useDBUser()
  const { user: clerkUser } = useUser()

  mixpanel.init('6ecc72f776e511873bde49db38df1e3b', {
    debug: false,
    track_pageview: true,
    persistence: 'localStorage',
  })

  useEffect(() => {
    if (!dbUser || !clerkUser) return

    mixpanel.identify(dbUser?.stripeCustomer ?? 'anonymous')
    posthog.identify(clerkUser?.primaryEmailAddress?.emailAddress, dbUser, clerkUser)

    mixpanel.register({
      $email: clerkUser?.primaryEmailAddress?.emailAddress,
      $name: clerkUser?.fullName,
    })
    console.log('emailAddress: ', clerkUser?.primaryEmailAddress?.emailAddress)
    mixpanel.people.set({
      $email: clerkUser?.primaryEmailAddress?.emailAddress,
      $name: clerkUser?.fullName,
    })
  }, [dbUser, clerkUser])

  return (
    <>
      <DefaultSeo
        defaultTitle="v0 report - automate company reports"
        title="v0 report AI"
        description="Use v0 report to generate company reports automatically"
        openGraph={{
          type: 'website',
          url: 'https://v0.report',
          siteName: 'v0 report',
          description: 'Use v0 report to generate company reports automatically',
          title: 'v0 report - automate company report',
          images: [
            {
              url: 'https://v0.report/images/header-image.png',
              alt: 'v0 report',
              type: 'image/png',
            },
          ],
        }}
      />
      <Toaster richColors />
      <LinkedInInsightTag partnerId={'5810098'} />
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <TopNav />
        <Component {...pageProps} />
      </QueryClientProvider>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=AW-16464288295`} />
      <GoogleTagManager gtmId="GTM-MX5KVVXZ" />
      <meta name="seobility" content="493dadeb99a11f7244021ea98ef446e9"></meta>
      <meta
        name="keywords"
        content="automate, company reports, v0 report, reporting tool, business analytics, swot reports, swot maker, swot generator, company analysis"
      />
    </>
  )
}

const YetAnotherWrapper = (props: AppProps) => {
  const { pathname } = useRouter()
  const isPricingPage = pathname.includes('pricing')

  return isPricingPage ? (
    <ClerkProviderReact publishableKey={PublicEnv.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}>
      <App {...props} />
    </ClerkProviderReact>
  ) : (
    <ClerkProvider {...props.pageProps}>
      <App {...props} />
    </ClerkProvider>
  )
}

export default trpc.withTRPC(YetAnotherWrapper)
